import React from 'react'
import { getStudentDropdownScore } from '../utils'
import 'styled-components/macro'
import { styled } from 'styled-components'
import {
	DropdownAnswer,
	ClozeDropdown,
	ClientFields,
	ImageLabeling,
	ClientQuestion,
	variantTypes,
} from '@mission.io/question-toolkit'
import { _tempTheme } from '../utils/tempTheme'

/**
 * The footer to display for a question when it is either being answered by a student (Student Prompt) or when it is being reviewed by a student (Student Instant Review)
 * @param {props} props passed to footer, the only required props are question and onClickSubmit.
 * @returns {JSX.Element}
 */
export default function AllStudentPromptFooters(props: {
	question: ClientQuestion
	isReview?: boolean
	error?: string | null
	onClickSubmit: () => void
	studentAnswer?: DropdownAnswer | undefined
}): JSX.Element {
	switch (props.question.type) {
		case variantTypes.CLOZE_DROPDOWN: {
			const question: ClozeDropdown<ClientFields> = props.question
			return <DropdownFooter {...props} question={question} />
		}
		case variantTypes.IMAGE_LABELING: {
			const question: ImageLabeling<ClientFields> = props.question
			return <DropdownFooter {...props} question={question} />
		}
		default: {
			return (
				<BasicSubmitFooter
					error={props.error}
					onClickSubmit={props.onClickSubmit}
					isReview={props.isReview}
				/>
			)
		}
	}
}

/**
 * The Footer for Prompt Student view or Instant Student review in ClozeDropdown/Image Labeling questions
 * @param {x: DropdownAnswer} studentAnswer
 * @param {DropdownClozeDropdown[]} dropdowns
 * @param {x: string} initialValue
 * @param {x: string | null} error
 * @param {x: () => void} onClickSubmit
 * @returns {JSX.Element}
 */
function DropdownFooter({
	studentAnswer,
	question,
	isReview,
	error,
	onClickSubmit,
}: {
	studentAnswer?: DropdownAnswer | undefined
	question: ClozeDropdown<ClientFields> | ImageLabeling<ClientFields>
	isReview?: boolean
	error?: string | null
	onClickSubmit: () => void
}): JSX.Element {
	let fractionText = 'N/A'
	if (isReview) {
		const score = getStudentDropdownScore(studentAnswer, question)
		if (score) fractionText = `${score.correct}/${score.total}`
	} else if (studentAnswer) {
		const solved = Object.keys(studentAnswer.value).length
		fractionText = `${solved}/${question.dropdowns.length}`
	}
	return (
		<FooterRow>
			<ChooseAnswer className="error">{error}</ChooseAnswer>
			<div className="content">
				<div className="item-selected">{fractionText}</div>
				<ButtonFooter onClick={onClickSubmit} $isReview={isReview}>
					Submit
				</ButtonFooter>
			</div>
		</FooterRow>
	)
}

/**
 * A basic footer for the Student prompt or Instant Student review for most question types.
 * @param {props.error} props.error error text
 * @param {props.onClickSubmit} props.onClickSubmit callback when student submits their answer
 * @param {props.isReview} props.isReview if a question is being reviewed
 * @returns {JSX.Element}
 */
function BasicSubmitFooter({
	error,
	onClickSubmit,
	isReview,
}: {
	error?: string | null
	onClickSubmit: () => any
	isReview?: boolean
}): JSX.Element {
	return (
		<FooterRow>
			<ChooseAnswer className="error">{error}</ChooseAnswer>
			<ButtonFooter onClick={onClickSubmit} $isReview={isReview}>
				Submit
			</ButtonFooter>
		</FooterRow>
	)
}

const ButtonFooter = styled.button<{ $isReview: boolean | undefined }>`
	padding: ${_tempTheme.spacing2} ${_tempTheme.spacing10};
	background-color: ${({ theme }) => `${theme.secondaryColor}`};
	color: white;
	font-family: Orbitron;
	font-weight: ${_tempTheme.font_bold};
	font-size: ${_tempTheme.text_lg};
	line-height: 23px;
	cursor: pointer;
	border: none;
	width: fit-content;
	margin: auto;
	margin-top: ${_tempTheme.spacing8};
	border-radius: ${_tempTheme.spacing1};
	${({ $isReview }) => $isReview && `visibility: hidden;`}
	${({ disabled }) => disabled && `opacity: 0.5; `}
`

const FooterRow = styled.div`
	align-items: center;
	margin: auto;
	background: ${({ theme }) => theme.darkPrimary};
	position: relative;

	.error {
		color: ${({ theme }) => theme.errorColor};
		position: absolute;
		width: 100%;
		left: 0px;
	}
	.content {
		width: 100%;
		position: relative;
	}
	div.item-selected {
		position: absolute;
		left: 0px;
		margin-top: ${_tempTheme.spacing1};
		color: ${({ theme }) => theme.white};
		font-family: Orbitron;
		font-style: normal;
		font-weight: ${_tempTheme.font_semibold};
		font-size: ${_tempTheme.text_2xl};
		line-height: 30px;
	}
`

export const ChooseAnswer = styled.div`
	margin: auto;
`
